/* eslint-disable react/require-default-props */
/**
 * @deprecated This component is deprecated and will be removed in a future release.
 * Please use the new Quimby UI component instead:
 *
 * Import: `import { Tooltip } from '@ui';`
 *
 * Or specific path: `import { Tooltip } from '@ui/atoms/Tooltip';`
 *
 * See the new component for documentation and migration notes.
 */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import './TooltipText.scss';
function TooltipText({ text, children, position = 'top', delay = 200, multiline = false }) {
    const [isVisible, setIsVisible] = useState(false);
    const [isPositioned, setIsPositioned] = useState(false);
    const [timeoutId, setTimeoutId] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0, position });
    const containerRef = useRef(null);
    const tooltipRef = useRef(null);
    useEffect(() => {
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [timeoutId]);
    const tooltipId = useMemo(() => `tooltip-${Math.random().toString(36).substr(2, 9)}`, []);
    const calculatePosition = () => {
        if (!containerRef.current || !tooltipRef.current)
            return;
        const container = containerRef.current.getBoundingClientRect();
        const tooltip = tooltipRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
        const scrollY = window.pageYOffset || document.documentElement.scrollTop;
        let bestPosition = position;
        let top = 0;
        let left = 0;
        switch (position) {
            case 'top':
                top = container.top + scrollY - tooltip.height - 8;
                left = container.left + scrollX + container.width / 2 - tooltip.width / 2;
                break;
            case 'bottom':
                top = container.bottom + scrollY + 8;
                left = container.left + scrollX + container.width / 2 - tooltip.width / 2;
                break;
            case 'left':
                top = container.top + scrollY + container.height / 2 - tooltip.height / 2;
                left = container.left + scrollX - tooltip.width - 8;
                break;
            case 'right':
                top = container.top + scrollY + container.height / 2 - tooltip.height / 2;
                left = container.right + scrollX + 8;
                break;
        }
        if (top < scrollY) {
            bestPosition = 'bottom';
            top = container.bottom + scrollY + 8;
        }
        else if (top + tooltip.height > scrollY + viewportHeight) {
            bestPosition = 'top';
            top = container.top + scrollY - tooltip.height - 8;
        }
        if (left < scrollX) {
            left = scrollX + 8;
        }
        else if (left + tooltip.width > scrollX + viewportWidth) {
            left = scrollX + viewportWidth - tooltip.width - 8;
        }
        setTooltipPosition({ top, left, position: bestPosition });
    };
    const handleMouseEnter = () => {
        const id = setTimeout(() => {
            setIsVisible(true);
            setIsPositioned(false);
            requestAnimationFrame(() => {
                calculatePosition();
                setIsPositioned(true);
            });
        }, delay);
        setTimeoutId(id);
    };
    const handleMouseLeave = () => {
        if (timeoutId) {
            clearTimeout(timeoutId);
            setTimeoutId(null);
        }
        setIsVisible(false);
        setIsPositioned(false);
    };
    const renderTooltipContent = () => {
        if (multiline && text.includes('\n')) {
            return text.split('\n').map((line, index, array) => (React.createElement(React.Fragment, { key: index },
                line,
                index < array.length - 1 && React.createElement("br", null))));
        }
        return text;
    };
    const tooltipElement = isVisible && (text === null || text === void 0 ? void 0 : text.length) > 0 ? (React.createElement("div", { ref: tooltipRef, id: tooltipId, className: `tooltipText__content tooltipText__content--${tooltipPosition.position} ${multiline ? 'tooltipText__content--multiline' : ''}`, style: {
            position: 'absolute',
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            zIndex: 10010,
            opacity: isPositioned ? 1 : 0,
            transition: 'opacity 0.1s ease-in-out'
        }, "aria-live": "polite" }, renderTooltipContent())) : null;
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { ref: containerRef, className: "tooltipText__container", onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, "aria-describedby": isVisible ? tooltipId : undefined }, children),
        isVisible && createPortal(tooltipElement, document.body)));
}
export default TooltipText;
